import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ShoppingCartIcon, HeartIcon } from 'lucide-react';
import { Product } from '../../types';
import { Badge, getBadgeVariant } from '../ui/Badge';
import { Rating } from '../ui/Rating';
import { useCart } from '../../context/CartContext';
interface ProductCardProps {
  product: Product;
  className?: string;
}
export function ProductCard({
  product,
  className = ''
}: ProductCardProps) {
  const {
    addToCart
  } = useCart();
  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('uk-UA').format(price);
  };
  const handleAddToCart = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    addToCart(product, 1);
  };
  return <motion.article whileHover={{
    y: -4
  }} transition={{
    duration: 0.2
  }} className={`group bg-[#0f0f0f] border border-[#1a1a1a] rounded-2xl hover:border-[#2a2a2a] transition-all duration-300 overflow-hidden flex flex-col h-full shadow-lg hover:shadow-xl ${className}`} data-id="element-262">
      <Link to={`/product/${product.id}`} className="block flex-1 flex flex-col" data-id="element-263">
        {/* Image Container */}
        <div className="relative aspect-square overflow-hidden bg-[#0a0a0a] flex-shrink-0 flex items-center justify-center border-b border-[#1a1a1a]" data-id="element-264">
          <div className="text-center p-8" data-id="element-265">
            <div className="w-24 h-24 bg-gradient-to-br from-[#1a1a1a] to-[#0f0f0f] rounded-full mx-auto mb-4 border border-[#1a1a1a]" data-id="element-266"></div>
            <p className="text-xs text-gray-600" data-id="element-267">Зображення товару</p>
          </div>

          {/* Badge */}
          {product.badge && <div className="absolute top-3 left-3" data-id="element-268">
              <Badge variant={getBadgeVariant(product.badge)} data-id="element-269">
                {product.badge}
              </Badge>
            </div>}

          {/* Quick Actions */}
          <div className="absolute top-3 right-3 flex flex-col gap-2 opacity-0 group-hover:opacity-100 transition-opacity duration-200" data-id="element-270">
            <motion.button whileTap={{
            scale: 0.9
          }} className="p-2 bg-[#0a0a0a]/95 backdrop-blur-sm border border-[#1a1a1a] rounded-full shadow-lg hover:bg-[#1a1a1a] hover:border-[#2a2a2a] transition-colors" aria-label="Додати в обране" onClick={e => {
            e.preventDefault();
            e.stopPropagation();
          }} data-id="element-271">
              <HeartIcon className="w-4 h-4 text-gray-400 hover:text-red-400 transition-colors" data-id="element-272" />
            </motion.button>
          </div>

          {/* Out of Stock Overlay */}
          {!product.inStock && <div className="absolute inset-0 bg-[#0a0a0a]/90 backdrop-blur-sm flex items-center justify-center" data-id="element-273">
              <span className="px-4 py-2 bg-[#1a1a1a] border border-[#2a2a2a] text-gray-400 text-sm font-medium rounded-lg" data-id="element-274">
                Немає в наявності
              </span>
            </div>}
        </div>

        {/* Content */}
        <div className="p-4 flex flex-col flex-1 bg-gradient-to-b from-[#0f0f0f] to-[#0a0a0a]" data-id="element-275">
          {/* Rating */}
          <div className="mb-3 h-5" data-id="element-276">
            <Rating value={product.rating} reviewCount={product.reviewCount} size="sm" data-id="element-277" />
          </div>

          {/* Name */}
          <h3 className="text-sm font-medium text-gray-200 mb-4 group-hover:text-white transition-colors flex-1 line-clamp-2 min-h-[2.5rem]" data-id="element-278">
            {product.name}
          </h3>

          {/* Price & Add to Cart */}
          <div className="mt-auto pt-3 border-t border-[#1a1a1a]" data-id="element-279">
            <div className="flex items-baseline gap-2 mb-3" data-id="element-280">
              <span className="text-lg font-bold text-white" data-id="element-281">
                {formatPrice(product.price)} ₴
              </span>
              {product.oldPrice && <span className="text-xs text-gray-600 line-through" data-id="element-282">
                  {formatPrice(product.oldPrice)} ₴
                </span>}
            </div>

            <div className="flex items-center justify-between gap-2" data-id="element-283">
              {product.inStock && <span className="text-xs text-emerald-400 font-medium" data-id="element-284">
                  В наявності
                </span>}

              {product.inStock && <motion.button whileTap={{
              scale: 0.95
            }} onClick={handleAddToCart} className="ml-auto p-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors shadow-md hover:shadow-lg" aria-label="Додати в кошик" data-id="element-285">
                  <ShoppingCartIcon className="w-4 h-4" data-id="element-286" />
                </motion.button>}
            </div>
          </div>
        </div>
      </Link>
    </motion.article>;
}